/**
 * AdminFieldCatalogue.jsx — read-only reference view of every booking
 * form field declared in /app/frontend/src/lib/booking-fields.js.
 *
 * Introduced 2026-07-14 alongside refactor R-7 (the source-of-truth
 * Map). Purpose: give admins, auditors, and new devs a live view of
 * every field on the booking form + its exact default / coercion /
 * hydration behaviour, so nobody has to grep three files to answer
 * "which fields do we store?" or "why is this column blank on save?".
 *
 * Read-only — no writes, no state beyond a search filter. Renders the
 * Map at page-load; hot-reload keeps it in sync while developing.
 */
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookOpen, Search } from "lucide-react";
import {
  ROOT_FIELDS,
  VEHICLE_REGOS_FIELDS,
  MASS_LIMITS_FIELDS,
  LOAD_RESTRAINT_FIELDS,
  TRAILER_WEIGHTS_FIELDS,
} from "@/lib/booking-fields";

const COERCE_TONE = {
  str:             "bg-slate-100  text-slate-700",
  orNull:          "bg-slate-100  text-slate-700",
  int:             "bg-blue-100   text-blue-700",
  num:             "bg-blue-100   text-blue-700",
  bool:            "bg-emerald-100 text-emerald-700",
  boolDefaultTrue: "bg-emerald-100 text-emerald-700",
  arr:             "bg-violet-100 text-violet-700",
  arrOrNull:       "bg-violet-100 text-violet-700",
  objOrEmpty:      "bg-amber-100  text-amber-700",
};

const HYDRATE_TONE = COERCE_TONE;

/** Format a default value for display — booleans, functions, strings, arrays. */
function fmtDefault(d) {
  if (typeof d === "function") {
    const sample = d();
    if (Array.isArray(sample)) return `() => []`;
    if (typeof sample === "object" && sample !== null) return `() => ({})`;
    return `() => ${JSON.stringify(sample)}`;
  }
  if (d === "") return `""`;
  if (d === null) return "null";
  if (typeof d === "boolean") return String(d);
  if (typeof d === "string")  return `"${d}"`;
  return String(d);
}

/** Format a coerce/hydrate preset for the pill. Function → "custom". */
function presetLabel(p) {
  if (typeof p === "function") return "custom";
  return p;
}

function PresetBadge({ preset, tone }) {
  const label = presetLabel(preset);
  const cls = tone[label] || "bg-slate-100 text-slate-700";
  return (
    <Badge variant="secondary" className={cls} data-testid={`preset-${label}`}>
      {label}
    </Badge>
  );
}

/** Table section: root columns, or one of the four JSONB parents. */
function FieldSection({ title, subtitle, rows, filter }) {
  const filtered = useMemo(() => {
    if (!filter) return rows;
    const q = filter.toLowerCase();
    return rows.filter(
      (r) =>
        r.formKey.toLowerCase().includes(q) ||
        (r.dbColumn || "").toLowerCase().includes(q)
    );
  }, [rows, filter]);

  if (filtered.length === 0) return null;

  return (
    <Card data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardHeader>
        <CardTitle className="text-base">
          {title}
          <span className="ml-2 text-xs font-normal text-slate-500">
            ({filtered.length}{filtered.length !== rows.length ? ` of ${rows.length}` : ""})
          </span>
        </CardTitle>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </CardHeader>
      <CardContent className="pt-0">
        <div className="rounded-md border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[24%]">Form key</TableHead>
                <TableHead className="w-[24%]">DB column</TableHead>
                <TableHead className="w-[22%]">Default</TableHead>
                <TableHead className="w-[15%]">Coerce</TableHead>
                <TableHead className="w-[15%]">Hydrate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.formKey} data-testid={`field-${r.formKey}`}>
                  <TableCell className="font-mono text-xs">{r.formKey}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{r.dbColumn}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{fmtDefault(r.default)}</TableCell>
                  <TableCell><PresetBadge preset={r.coerce} tone={COERCE_TONE} /></TableCell>
                  <TableCell><PresetBadge preset={r.hydrate} tone={HYDRATE_TONE} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminFieldCatalogue() {
  const [filter, setFilter] = useState("");

  // Normalise every source array into the same row shape.
  const rootRows = useMemo(
    () => ROOT_FIELDS.map((f) => ({
      formKey: f.key, dbColumn: f.key,
      default: f.default, coerce: f.coerce, hydrate: f.hydrate,
    })),
    []
  );
  const jsonbSections = useMemo(() => [
    {
      title: "vehicle_regos (JSONB)",
      subtitle: "Vehicle registration numbers nested under the vehicle_regos JSONB column.",
      rows: VEHICLE_REGOS_FIELDS.map((f) => ({
        formKey: f.form, dbColumn: `vehicle_regos.${f.jsonb}`,
        default: f.default, coerce: f.coerce, hydrate: f.hydrate,
      })),
    },
    {
      title: "mass_limits (JSONB)",
      subtitle: "GVM / axle-mass limits + optional notes nested under mass_limits.",
      rows: MASS_LIMITS_FIELDS.map((f) => ({
        formKey: f.form, dbColumn: `mass_limits.${f.jsonb}`,
        default: f.default, coerce: f.coerce, hydrate: f.hydrate,
      })),
    },
    {
      title: "load_restraint (JSONB)",
      subtitle: "Restraint method + flag booleans nested under load_restraint.",
      rows: LOAD_RESTRAINT_FIELDS.map((f) => ({
        formKey: f.form, dbColumn: `load_restraint.${f.jsonb}`,
        default: f.default, coerce: f.coerce, hydrate: f.hydrate,
      })),
    },
    {
      title: "trailer_weights (JSONB)",
      subtitle: "Per-trailer weight readings nested under trailer_weights.",
      rows: TRAILER_WEIGHTS_FIELDS.map((f) => ({
        formKey: f.form, dbColumn: `trailer_weights.${f.jsonb}`,
        default: f.default, coerce: f.coerce, hydrate: f.hydrate,
      })),
    },
  ], []);

  const totalFields =
    rootRows.length +
    jsonbSections.reduce((n, s) => n + s.rows.length, 0);

  return (
    <div className="space-y-6" data-testid="field-catalogue-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-slate-500" />
            Field catalogue
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Live reference of every field on the booking form — sourced
            directly from <code className="text-xs bg-slate-100 px-1 rounded">/lib/booking-fields.js</code>.
            Adding a new column to <code className="text-xs bg-slate-100 px-1 rounded">lbs_bookings</code>?
            Add one entry there and defaults, payload coercion, and form
            hydration all update automatically.
          </p>
        </div>
        <Badge variant="secondary" className="text-sm shrink-0">
          {totalFields} fields
        </Badge>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="field-catalogue-search"
          aria-label="Filter fields by form key or database column"
          placeholder="Filter by form key or DB column…"
          className="pl-9"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <FieldSection
        title="Root columns"
        subtitle="Direct columns on lbs_bookings — the bulk of the form."
        rows={rootRows}
        filter={filter}
      />

      {jsonbSections.map((s) => (
        <FieldSection
          key={s.title}
          title={s.title}
          subtitle={s.subtitle}
          rows={s.rows}
          filter={filter}
        />
      ))}

      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6 text-xs text-slate-600 space-y-2">
          <p>
            <strong>Coerce presets</strong> (form → DB payload):
            <span className="font-mono ml-2">str</span> passthrough (empty → null downstream) ·
            <span className="font-mono ml-2">orNull</span> `v || null` ·
            <span className="font-mono ml-2">int / num</span> parse or null ·
            <span className="font-mono ml-2">bool</span> `!!v` ·
            <span className="font-mono ml-2">arr</span> always-array ·
            <span className="font-mono ml-2">arrOrNull</span> collapse empty to null ·
            <span className="font-mono ml-2">objOrEmpty</span> `v || {}` ·
            <span className="font-mono ml-2">boolDefaultTrue</span> `v !== false`.
          </p>
          <p>
            <strong>Hydrate presets</strong> (DB → form values):
            <span className="font-mono ml-2">str</span> `v || ""` ·
            <span className="font-mono ml-2">num</span> `v ?? ""` (preserves 0) ·
            <span className="font-mono ml-2">bool</span> `!!v` ·
            <span className="font-mono ml-2">arr</span> `v || []` ·
            <span className="font-mono ml-2">obj</span> `v || {}` ·
            <span className="font-mono ml-2">boolDefaultTrue</span> `v !== false`.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
